// Animation state of one character, as ET splits it between the game and
// cgame, run here on the client for every drawn player:
//
// - "game" side (bg_animation.c, normally in playerState_t): the legs and
//   torso animation numbers with their toggle bit and the timers that let
//   events (jump) block the locomotion animations for their duration -
//   BG_AnimScriptAnimation, BG_AnimScriptEvent, BG_PlayAnim.
// - cgame side (cg_players.c): the lerp frames that play those animations at
//   a rate following the real movement speed (CG_SetLerpFrameAnimationRate,
//   CG_RunLerpFrameRate), the turn-in-place animation and the legs / torso /
//   head angles (CG_PlayerAnimation, CG_SwingAngles, CG_PlayerAngles).
//
// The server tells which movetype PM_Footsteps asked for and which events
// pmove raised (protocol.ts RemotePlayer); everything else is computed here.

import type { Vec3 } from '../../shared/math';
import { ANIMFL_FIRINGANIM, ANIMFL_LADDERANIM, ANIMFL_REVERSED, type Animation } from './animGroup';
import { ANIM_BP, ANIM_COND, ANIM_ET, ANIM_MT, CondBits, Conditions, firstValidItem, movetypeItem, nameIndex, type AnimModel, type ScriptCommand } from './animScript';
import { anglesToAxis, type Axis } from '../../render/models/skeleton';

export const ANIM_TOGGLEBIT = 1 << 9;
const SWING_RIGHT = 1;
const SWING_LEFT = 2;
/** cg_swingSpeed */
const SWING_SPEED = 0.1;
const ANIM_SCALEMAX_LOW = 1.1;
const ANIM_SCALEMAX_HIGH = 1.6;
const ANIM_SPEEDMAX_LOW = 100;
const ANIM_SPEEDMAX_HIGH = 20;
const TURN_BITS = (1 << ANIM_MT.TURNLEFT) | (1 << ANIM_MT.TURNRIGHT);
const CROUCH_BITS = (1 << ANIM_MT.IDLECR) | (1 << ANIM_MT.WALKCR) | (1 << ANIM_MT.WALKCRBK);

/** AngleSubtract */
function angleSubtract(a1: number, a2: number): number {
  let a = a1 - a2;
  while (a > 180) a -= 360;
  while (a < -180) a += 360;
  return a;
}
/** AngleMod */
const angleMod = (a: number) => (360 / 65536) * (Math.trunc(a * (65536 / 360)) & 65535);

interface LerpFrame {
  oldFrame: number;
  oldFrameModel: string;
  oldFrameTime: number;
  frame: number;
  frameModel: string;
  frameTime: number;
  backlerp: number;
  yawAngle: number;
  yawing: number;
  pitchAngle: number;
  pitching: number;
  animationNumber: number;
  oldAnimationNumber: number;
  animation: Animation | null;
  animationTime: number;
  oldFramePos: Vec3;
  animSpeedScale: number;
  oldFrameSnapshotTime: number;
}

const newLerpFrame = (): LerpFrame => ({
  oldFrame: 0,
  oldFrameModel: '',
  oldFrameTime: 0,
  frame: 0,
  frameModel: '',
  frameTime: 0,
  backlerp: 0,
  yawAngle: 0,
  yawing: 0,
  pitchAngle: 0,
  pitching: 0,
  animationNumber: 0,
  oldAnimationNumber: 0,
  animation: null,
  animationTime: 0,
  oldFramePos: [0, 0, 0],
  animSpeedScale: 1,
  oldFrameSnapshotTime: 0,
});

/** What the renderer needs each frame (a refEntity_t's animation part). */
export interface AnimPose {
  frameModel: string;
  frame: number;
  oldFrameModel: string;
  oldFrame: number;
  backlerp: number;
  torsoFrameModel: string;
  torsoFrame: number;
  oldTorsoFrameModel: string;
  oldTorsoFrame: number;
  torsoBacklerp: number;
  /** Legs (entity) axis in the world. */
  legsAxis: Axis;
  /** Torso relative to the legs. */
  torsoAxis: Axis;
  /** Head relative to the torso. */
  headAxis: Axis;
}

/** Per frame input: the player as drawn. */
export interface AnimFrameInput {
  /** Client time, ms. */
  time: number;
  /** ms since the previous frame (cg.frametime). */
  frametime: number;
  /** Drawn (interpolated) origin and view angles. */
  origin: Vec3;
  angles: Vec3;
  velocity: Vec3;
  /** Legs yaw offset from the view (ps->movementDir). */
  movementDir: number;
  prone: boolean;
  crouched: boolean;
  /** Time (id) of the latest state received; the movement speed is measured once per state. */
  snapshotTime: number;
}

export class PlayerAnimator {
  // --- game side (playerState_t) ---
  legsAnim = 0;
  torsoAnim = 0;
  legsTimer = 0;
  torsoTimer = 0;
  readonly aiState = 0;
  /** Conditions the script runs with on the game side (movetype = last locomotion played). */
  readonly conditions = new Conditions();

  // --- cgame side ---
  /** Conditions as cgame knows them (CG_AnimPlayerConditions: movetype from the legs animation). */
  private readonly clientConditions = new Conditions();
  private readonly legs = newLerpFrame();
  private readonly torso = newLerpFrame();
  private initialized = false;
  private readonly weapon: number;

  constructor(
    readonly model: AnimModel,
    /** clientNum: picks between alternative locomotion commands. */
    readonly clientNum: number,
    weapon = 'knife',
  ) {
    this.weapon = nameIndex(model, ANIM_COND.WEAPON, weapon);
    this.conditions.set(ANIM_COND.WEAPON, this.weapon);
    this.clientConditions.set(ANIM_COND.WEAPON, this.weapon);
    // the first locomotion of a spawned player
    this.locomotion(ANIM_MT.IDLE);
  }

  private get anims(): Animation[] {
    return this.model.animations;
  }

  // --- bg_animation.c ---

  /** BG_PlayAnim: returns the duration, or -1 when the legs did not take the animation. */
  private playAnim(animNum: number, bodyPart: number, forceDuration: number, setTimer: boolean, isContinue: boolean, force: boolean): number {
    const anim = this.anims[animNum];
    const duration = forceDuration || anim.duration + 50;
    let wasSet = false;
    if (bodyPart === ANIM_BP.BOTH || bodyPart === ANIM_BP.LEGS) {
      if (this.legsTimer < 50 || force) {
        if (!isContinue || (this.legsAnim & ~ANIM_TOGGLEBIT) !== animNum) {
          wasSet = true;
          this.legsAnim = ((this.legsAnim & ANIM_TOGGLEBIT) ^ ANIM_TOGGLEBIT) | animNum;
          if (setTimer) this.legsTimer = duration;
        } else if (setTimer && anim.loopFrames) this.legsTimer = duration;
      }
    }
    if (bodyPart === ANIM_BP.BOTH || bodyPart === ANIM_BP.TORSO) {
      if (this.torsoTimer < 50 || force) {
        if (!isContinue || (this.torsoAnim & ~ANIM_TOGGLEBIT) !== animNum) {
          this.torsoAnim = ((this.torsoAnim & ANIM_TOGGLEBIT) ^ ANIM_TOGGLEBIT) | animNum;
          if (setTimer) this.torsoTimer = duration;
        } else if (setTimer && anim.loopFrames) this.torsoTimer = duration;
      }
    }
    return wasSet ? duration : -1;
  }

  /** BG_ExecuteCommand */
  private executeCommand(cmd: ScriptCommand, setTimer: boolean, isContinue: boolean, force: boolean): number {
    let duration = -1;
    let playedLegs = false;
    for (let k = 0; k < 2; k++) {
      const part = cmd.bodyPart[k];
      if (!part) continue;
      // (sic) both parts use the first part's duration
      duration = cmd.animDuration[0] + 50;
      const r = this.playAnim(cmd.animIndex[k], part, duration, setTimer, isContinue, force);
      if (part === ANIM_BP.BOTH || part === ANIM_BP.LEGS) playedLegs = r > -1;
    }
    return playedLegs ? duration : -1;
  }

  /** BG_AnimScriptAnimation: 1 set, 0 already playing / blocked, -1 no animation for this movetype. */
  animScriptAnimation(movetype: number, isContinue = true): number {
    const item = movetypeItem(this.model, this.conditions, this.aiState, movetype);
    if (!item) return -1;
    this.conditions.set(ANIM_COND.MOVETYPE, movetype);
    const cmd = item.commands[this.clientNum % item.commands.length];
    return this.executeCommand(cmd, false, isContinue, false) !== -1 ? 1 : 0;
  }

  /** BG_AnimScriptEvent: the duration, or -1. */
  animScriptEvent(event: number, isContinue = false, force = false): number {
    const item = firstValidItem(this.conditions, this.model.scriptEvents[event]);
    if (!item) return -1;
    const cmd = item.commands[Math.floor(Math.random() * item.commands.length)];
    return this.executeCommand(cmd, true, isContinue, force);
  }

  /** What PM_Footsteps did: a movetype, and the one it falls back to when the script has none. */
  locomotion(movetype: number, fallback = 0): void {
    if (!movetype) return;
    if (this.animScriptAnimation(movetype) < 0 && fallback) this.animScriptAnimation(fallback);
  }

  /** Jump (forward / backwards) and land events of pmove (see shared/game/movetype.ts). */
  jump(back: boolean, force: boolean): void {
    this.animScriptEvent(back ? ANIM_ET.JUMPBK : ANIM_ET.JUMP, false, force);
  }

  /** PM_CrashLand: only when no event animation is playing on the legs. */
  land(): void {
    if (!this.legsTimer) this.animScriptEvent(ANIM_ET.LAND, false, true);
  }

  /** PmoveSingle drops the animation timers by the command length. */
  advanceTimers(msec: number): void {
    if (this.legsTimer > 0) this.legsTimer = Math.max(0, this.legsTimer - msec);
    if (this.torsoTimer > 0) this.torsoTimer = Math.max(0, this.torsoTimer - msec);
  }

  // --- cg_players.c ---

  private isCrouchingAnim(animNum: number): boolean {
    const anim = this.anims[animNum & ~ANIM_TOGGLEBIT];
    return !!anim && (anim.movetype & CROUCH_BITS) !== 0;
  }

  /** CG_SetLerpFrameAnimationRate */
  private setLerpFrameAnimation(lf: LerpFrame, newAnimation: number, time: number): void {
    const oldanim = lf.animation;
    const oldAnimNum = lf.animationNumber;
    const firstAnim = !lf.animation;
    lf.animationNumber = newAnimation;
    const anim = this.anims[newAnimation & ~ANIM_TOGGLEBIT];
    lf.animation = anim;
    lf.animationTime = lf.frameTime + anim.initialLerp;
    if (!(anim.flags & ANIMFL_FIRINGANIM) || lf !== this.torso) {
      let transitionMin: number;
      if (lf === this.legs && this.isCrouchingAnim(newAnimation) !== this.isCrouchingAnim(oldAnimNum)) {
        transitionMin = lf.frameTime + (anim.moveSpeed || anim.movetype & TURN_BITS ? 200 : 350);
      } else if (anim.moveSpeed) transitionMin = lf.frameTime + 120;
      else transitionMin = lf.frameTime + 170;
      if (oldanim && oldanim.animBlend) {
        transitionMin = lf.frameTime + oldanim.animBlend;
        lf.animationTime = transitionMin;
      } else {
        if (anim.moveSpeed && lf.animSpeedScale < 1.0) lf.animationTime += anim.initialLerp;
        if (lf.animationTime < transitionMin) lf.animationTime = transitionMin;
      }
    }
    if (firstAnim) {
      lf.frameTime = time - 1;
      lf.animationTime = time - 1;
      lf.frame = anim.firstFrame;
      lf.frameModel = anim.mdxFile;
    }
  }

  /** CG_RunLerpFrameRate */
  private runLerpFrame(lf: LerpFrame, newAnimation: number, input: AnimFrameInput, snapOrigin: Vec3, recursion: number): void {
    const time = input.time;
    const isLadderAnim = !!lf.animation && (lf.animation.flags & ANIMFL_LADDERANIM) !== 0;
    const oldAnim = lf.animation;
    if (newAnimation !== lf.animationNumber || !lf.animation) this.setLerpFrameAnimation(lf, newAnimation, time);
    const anim = lf.animation!;

    if (anim.moveSpeed && lf.oldFrameSnapshotTime) {
      // the speed moved over the last frame, once per received state
      if (input.snapshotTime !== lf.oldFrameSnapshotTime) {
        if (isLadderAnim) {
          // only use the Z axis for the speed
          lf.oldFramePos[0] = snapOrigin[0];
          lf.oldFramePos[1] = snapOrigin[1];
        }
        const o = input.origin;
        const dist = Math.hypot(o[0] - lf.oldFramePos[0], o[1] - lf.oldFramePos[1], o[2] - lf.oldFramePos[2]);
        const moveSpeed = dist / ((time - lf.oldFrameTime) / 1000);
        lf.animSpeedScale = Number.isFinite(moveSpeed) ? moveSpeed / anim.moveSpeed : 1;
        lf.oldFrameSnapshotTime = input.snapshotTime;
      }
    } else {
      lf.animSpeedScale = 1.0;
      lf.oldFrameSnapshotTime = input.snapshotTime;
    }

    if (time >= lf.frameTime) {
      lf.oldFrame = lf.frame;
      lf.oldFrameTime = lf.frameTime;
      lf.oldFrameModel = lf.frameModel;
      lf.oldFramePos = [input.origin[0], input.origin[1], input.origin[2]];

      // restrict the speed range
      if (lf.animSpeedScale < 0.25) {
        lf.animSpeedScale = lf.animSpeedScale < 0.01 && isLadderAnim ? 0.0 : 0.25;
      } else if (lf.animSpeedScale > ANIM_SCALEMAX_LOW) {
        if (!(anim.flags & ANIMFL_LADDERANIM)) {
          // allow slower anims to speed up more than faster anims
          if (anim.moveSpeed > ANIM_SPEEDMAX_LOW) lf.animSpeedScale = ANIM_SCALEMAX_LOW;
          else if (anim.moveSpeed < ANIM_SPEEDMAX_HIGH) {
            if (lf.animSpeedScale > ANIM_SCALEMAX_HIGH) lf.animSpeedScale = ANIM_SCALEMAX_HIGH;
          } else {
            lf.animSpeedScale = ANIM_SCALEMAX_HIGH - ((ANIM_SCALEMAX_HIGH - ANIM_SCALEMAX_LOW) * (anim.moveSpeed - ANIM_SPEEDMAX_HIGH)) / (ANIM_SPEEDMAX_LOW - ANIM_SPEEDMAX_HIGH);
          }
        } else if (lf.animSpeedScale > 4.0) lf.animSpeedScale = 4.0;
      }

      const otherLf = lf === this.legs ? this.torso : this.legs;
      const otherAnim = otherLf.animation;
      let f: number;
      if (!lf.animSpeedScale) {
        // stopped on the ladder, so stay on the same frame
        f = lf.frame - anim.firstFrame;
        lf.frameTime += anim.frameLerp;
      } else if (lf.oldAnimationNumber !== lf.animationNumber && (!anim.moveSpeed || lf.oldFrame < anim.firstFrame || lf.oldFrame >= anim.firstFrame + anim.numFrames)) {
        lf.frameTime = lf.animationTime; // initial lerp
        if (oldAnim && anim.moveSpeed) {
          // keep locomotions going continuously
          f = lf.frame - oldAnim.firstFrame + 1;
          while (f < 0) f += anim.numFrames;
        } else f = 0;
      } else if (
        lf === this.legs &&
        otherAnim &&
        !(anim.flags & ANIMFL_FIRINGANIM) &&
        (lf.animationNumber & ~ANIM_TOGGLEBIT) === (this.torso.animationNumber & ~ANIM_TOGGLEBIT) &&
        !anim.moveSpeed
      ) {
        // legs should sync with torso
        f = this.torso.frame - otherAnim.firstFrame;
        if (f >= anim.numFrames || f < 0) f = 0;
        lf.frameTime = this.torso.frameTime;
      } else if (
        lf === this.torso &&
        otherAnim &&
        !(anim.flags & ANIMFL_FIRINGANIM) &&
        (lf.animationNumber & ~ANIM_TOGGLEBIT) === (this.legs.animationNumber & ~ANIM_TOGGLEBIT) &&
        otherAnim.moveSpeed
      ) {
        // torso needs to sync with legs
        f = this.legs.frame - otherAnim.firstFrame;
        if (f >= anim.numFrames || f < 0) f = 0;
        lf.frameTime = this.legs.frameTime;
      } else {
        lf.frameTime = lf.oldFrameTime + Math.trunc(anim.frameLerp * (1.0 / lf.animSpeedScale));
        if (lf.frameTime < time) lf.frameTime = time;
        // check for skipping frames (death anims play in slo-mo at low framerates)
        const skip = time > lf.frameTime && !anim.moveSpeed ? 1 + Math.trunc((time - lf.frameTime) / anim.frameLerp) : 1;
        if (anim.flags & ANIMFL_REVERSED) f = anim.numFrames - 1 - (lf.frame - anim.firstFrame - skip);
        else f = lf.frame - anim.firstFrame + skip;
        if (f < 0) f = 0;
      }
      if (f >= anim.numFrames) {
        f -= anim.numFrames;
        if (anim.loopFrames) {
          f %= anim.loopFrames;
          f += anim.numFrames - anim.loopFrames;
        } else {
          f = anim.numFrames - 1;
          // stuck at the end: can transition to another sequence right away
          lf.frameTime = time;
        }
      }
      lf.frame = anim.flags & ANIMFL_REVERSED ? anim.firstFrame + anim.numFrames - 1 - f : anim.firstFrame + f;
      lf.frameModel = anim.mdxFile;
      if (time > lf.frameTime) {
        // run the frame again until we move ahead of the current time
        if (recursion > 4) lf.frameTime = time;
        else this.runLerpFrame(lf, newAnimation, input, snapOrigin, recursion + 1);
      }
      lf.oldAnimationNumber = lf.animationNumber;
    }

    if (lf.frameTime > time + 5000) lf.frameTime = time;
    if (lf.oldFrameTime > time) lf.oldFrameTime = time;
    lf.backlerp = lf.frameTime === lf.oldFrameTime ? 0 : 1.0 - (time - lf.oldFrameTime) / (lf.frameTime - lf.oldFrameTime);
  }

  /** CG_ClearLerpFrameRate + CG_ResetPlayerEntity: a player that just appeared or teleported. */
  reset(time: number, angles: Vec3): void {
    for (const [lf, anim] of [
      [this.legs, this.legsAnim],
      [this.torso, this.torsoAnim],
    ] as const) {
      Object.assign(lf, newLerpFrame());
      lf.frameTime = lf.oldFrameTime = time;
      this.setLerpFrameAnimation(lf, anim, time);
      lf.oldFrame = lf.frame = lf.animation!.firstFrame;
      lf.oldFrameModel = lf.frameModel = lf.animation!.mdxFile;
    }
    this.legs.yawAngle = angles[1];
    this.torso.yawAngle = angles[1];
    this.torso.pitchAngle = angles[0];
    this.initialized = true;
  }

  /** CG_SwingAngles on a lerp frame's yaw or pitch. */
  private swing(lf: LerpFrame, which: 'yaw' | 'pitch', destination: number, swingTolerance: number, clampTolerance: number, speed: number, frametime: number): void {
    const key = which === 'yaw' ? 'yawAngle' : 'pitchAngle';
    const flag = which === 'yaw' ? 'yawing' : 'pitching';
    let angle = lf[key];
    if (!lf[flag]) {
      // see if a swing should be started
      const s = angleSubtract(angle, destination);
      if (s > swingTolerance || s < -swingTolerance) lf[flag] = 1;
    }
    if (!lf[flag]) return;
    // modify the speed depending on the delta, so it doesn't seem so linear
    const s = angleSubtract(destination, angle);
    let scale = Math.abs(s) * 0.05;
    if (scale < 0.5) scale = 0.5;
    if (s >= 0) {
      let move = frametime * scale * speed;
      if (move >= s) {
        move = s;
        lf[flag] = 0;
      } else lf[flag] = SWING_LEFT;
      angle = angleMod(angle + move);
    } else {
      let move = frametime * scale * -speed;
      if (move <= s) {
        move = s;
        lf[flag] = 0;
      } else lf[flag] = SWING_RIGHT;
      angle = angleMod(angle + move);
    }
    // clamp to no more than tolerance
    const d = angleSubtract(destination, angle);
    if (d > clampTolerance) angle = angleMod(destination - (clampTolerance - 1));
    else if (d < -clampTolerance) angle = angleMod(destination + (clampTolerance - 1));
    lf[key] = angle;
  }

  /** CG_PlayerAngles: legs angles in the world, torso relative to the legs, head relative to the torso. */
  private playerAngles(input: AnimFrameInput): { legs: Vec3; torso: Vec3; head: Vec3 } {
    const c = this.clientConditions;
    const ft = input.frametime;
    const head: Vec3 = [input.angles[0], angleMod(input.angles[1]), input.angles[2]];
    const legs: Vec3 = [0, 0, 0];
    const torso: Vec3 = [0, 0, 0];

    // yaw: allow it to drift a bit only while idle
    if (!(c.hasBit(ANIM_COND.MOVETYPE, ANIM_MT.IDLE) || c.hasBit(ANIM_COND.MOVETYPE, ANIM_MT.IDLECR))) {
      this.torso.yawing = 1;
      this.torso.pitching = 1;
      this.legs.yawing = 1;
    }
    legs[1] = head[1] + input.movementDir;
    torso[1] = head[1] + 0.35 * input.movementDir;
    this.swing(this.torso, 'yaw', torso[1], 25, 90, SWING_SPEED, ft);
    const clampTolerance = 150;
    const legsAnim = this.anims[this.legsAnim & ~ANIM_TOGGLEBIT];
    if (c.hasBit(ANIM_COND.MOVETYPE, ANIM_MT.IDLE)) {
      this.legs.yawing = 0; // set it if they really need to swing
      this.swing(this.legs, 'yaw', legs[1], 20, clampTolerance, 0.5 * SWING_SPEED, ft);
    } else if (legsAnim?.name.includes('strafe')) {
      this.legs.yawing = 0;
      legs[1] = head[1];
      this.swing(this.legs, 'yaw', legs[1], 0, clampTolerance, SWING_SPEED, ft);
    } else if (this.legs.yawing) {
      this.swing(this.legs, 'yaw', legs[1], 0, clampTolerance, SWING_SPEED, ft);
    } else {
      this.swing(this.legs, 'yaw', legs[1], 40, clampTolerance, SWING_SPEED, ft);
    }
    torso[1] = this.torso.yawAngle;
    legs[1] = this.legs.yawAngle;

    // pitch: only a fraction of it in the torso
    const dest = (head[0] > 180 ? -360 + head[0] : head[0]) * 0.75;
    if (input.prone) torso[0] = legs[0] - 3;
    else {
      this.swing(this.torso, 'pitch', dest, 15, 30, 0.1, ft);
      torso[0] = this.torso.pitchAngle;
    }

    // roll: lean towards the direction of travel
    const v = input.velocity;
    let speed = Math.hypot(v[0], v[1], v[2]);
    if (speed) {
      const dir: Vec3 = [v[0] / speed, v[1] / speed, v[2] / speed];
      speed *= 0.05;
      const axis = anglesToAxis(legs);
      legs[2] -= speed * (dir[0] * axis[1][0] + dir[1] * axis[1][1] + dir[2] * axis[1][2]);
      legs[0] += speed * (dir[0] * axis[0][0] + dir[1] * axis[0][1] + dir[2] * axis[0][2]);
    }

    // pull the angles back out of the hierarchical chain
    const headRel: Vec3 = [angleSubtract(head[0], torso[0]), angleSubtract(head[1], torso[1]), angleSubtract(head[2], torso[2])];
    const torsoRel: Vec3 = [angleSubtract(torso[0], legs[0]), angleSubtract(torso[1], legs[1]), angleSubtract(torso[2], legs[2])];
    return { legs, torso: torsoRel, head: headRel };
  }

  /** CG_AnimPlayerConditions */
  private updateClientConditions(input: AnimFrameInput): void {
    const c = this.clientConditions;
    c.set(ANIM_COND.WEAPON, this.weapon);
    c.set(ANIM_COND.MOUNTED, 0);
    c.set(ANIM_COND.UNDERHAND, input.angles[0] > 0 ? 1 : 0);
    c.set(ANIM_COND.CROUCHING, input.crouched && !input.prone ? 1 : 0);
    c.set(ANIM_COND.FIRING, 0);
    // reverse engineer the legs anim -> movetype (if possible)
    const mask = this.anims[this.legsAnim & ~ANIM_TOGGLEBIT]?.movetype ?? 0;
    if (mask) {
      const bits = new CondBits();
      for (let b = 0; b < 31; b++) if (mask & (1 << b)) bits.set.add(b);
      c.bits[ANIM_COND.MOVETYPE] = bits;
    }
    // the game side conditions the script reads besides the movetype
    this.conditions.set(ANIM_COND.UNDERHAND, input.angles[0] > 0 ? 1 : 0);
  }

  /** BG_GetAnimScriptAnimation for the turn animations (client conditions). */
  private turnAnimation(movetype: number): number {
    const item = movetypeItem(this.model, this.clientConditions, this.aiState, movetype);
    if (!item) return -1;
    const cmd = item.commands[this.clientNum % item.commands.length];
    return cmd.bodyPart[0] ? cmd.animIndex[0] : -1;
  }

  /** CG_Player's animation part for one frame: angles, conditions, then the lerp frames. */
  frame(input: AnimFrameInput, snapOrigin: Vec3 = input.origin): AnimPose {
    if (!this.initialized) this.reset(input.time, input.angles);
    const angles = this.playerAngles(input);
    this.updateClientConditions(input);

    // CG_PlayerAnimation: shuffle turn frames are done locally
    let animIndex = this.legsAnim;
    if (this.legs.yawing) {
      const turn = this.turnAnimation(this.legs.yawing === SWING_RIGHT ? ANIM_MT.TURNRIGHT : ANIM_MT.TURNLEFT);
      if (turn > -1) animIndex = turn;
    }
    this.runLerpFrame(this.legs, animIndex, input, snapOrigin, 0);
    this.runLerpFrame(this.torso, this.torsoAnim, input, snapOrigin, 0);
    return {
      frameModel: this.legs.frameModel,
      frame: this.legs.frame,
      oldFrameModel: this.legs.oldFrameModel || this.legs.frameModel,
      oldFrame: this.legs.oldFrame,
      backlerp: this.legs.backlerp,
      torsoFrameModel: this.torso.frameModel,
      torsoFrame: this.torso.frame,
      oldTorsoFrameModel: this.torso.oldFrameModel || this.torso.frameModel,
      oldTorsoFrame: this.torso.oldFrame,
      torsoBacklerp: this.torso.backlerp,
      legsAxis: anglesToAxis(angles.legs),
      torsoAxis: anglesToAxis(angles.torso),
      headAxis: anglesToAxis(angles.head),
    };
  }

  /** Name of the legs / torso animations being played (debugging, tests). */
  get current(): { legs: string; torso: string } {
    return { legs: this.anims[this.legsAnim & ~ANIM_TOGGLEBIT]?.name ?? '', torso: this.anims[this.torsoAnim & ~ANIM_TOGGLEBIT]?.name ?? '' };
  }
}
